import { ChangeDetectionStrategy, Component, OnInit, afterNextRender, computed, inject, input, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { IonicModule, ModalController } from '@ionic/angular';
import { Components } from '@ionic/core';

import Fuse from 'fuse.js';
import { sum } from 'lodash';
import { LOGGER, Logger } from '../../util/logger';
import { UiOptions } from '../../util/ui-options';
import { verticalCollapseAnimation } from '../../util/vertical-collapse-animation';

export interface CategorySelectModalItem {
    key: any;
    name: string;
    disabled?: boolean;
}

export interface CategorySelectModalCategory {
    name: string;
    items: CategorySelectModalItem[];
    expanded?: boolean;
}

export interface CategorySelectModalArgs {
    title: string;
    cancelButton?: string;
    choices: CategorySelectModalCategory[];
    initialValue?: string;
    dismissOnClick?: boolean;
    mode: 'ios' | 'md' | undefined;
    canSearch?: boolean;
    defaultValues?: any[];
}

interface SearchableItem {
    item: CategorySelectModalItem;
    category: CategorySelectModalCategory;
}

@Component({
    selector: 'category-select-modal',
    imports: [FormsModule, IonicModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @let canSearch = effectiveCanSearch();
        @let choices = visibleChoices();
        @let titleText = title();
        @let cancelText = cancelButton();
        @let currentMode = mode();
        @let selectedValue = value();

        <ion-header>
            <ion-toolbar
                [color]="uiOptions.toolbarColor()"
                [class.toolbar-portrait-padding-md]="uiOptions.isAndroid() && uiOptions.orientation().includes('portrait')"
                [class.landscape-horizontal-padding]="uiOptions.isAndroid() && uiOptions.orientation().includes('landscape')"
            >
                <ion-title>{{ titleText }}</ion-title>
                <ion-buttons slot="end">
                    @if (cancelText) {
                        <ion-button (click)="cancel()"> {{ cancelText }} </ion-button>
                    } @else {
                        <ion-button (click)="cancel()">
                            <ion-icon slot="icon-only" name="close"></ion-icon>
                        </ion-button>
                    }
                </ion-buttons>
            </ion-toolbar>
            @if (canSearch) {
                <ion-toolbar class="search-bar">
                    <ion-searchbar
                        #theSearchbar
                        showCancelButton="never"
                        [ngModel]="searchQuery()"
                        (ngModelChange)="searchQuery.set($event)"
                        (keydown)="onSearchBarKeyDown($event)"
                    >
                    </ion-searchbar>
                </ion-toolbar>
            }
        </ion-header>
        <ion-content>
            <ion-list [class.landscape-horizontal-padding]="uiOptions.isAndroid() && uiOptions.orientation().includes('landscape')">
                <ion-radio-group [value]="selectedValue">
                    @if (defaultValues(); as defaults) {
                        <ion-item class="reset-item" (click)="selectValue(defaults)" detail="false" lines="full">
                            <ion-icon slot="start" name="refresh-outline" color="primary"></ion-icon>
                            <ion-label color="primary">Reset to Defaults</ion-label>
                        </ion-item>
                    }
                    @for (category of choices; track category.name) {
                        @if (choices.length > 1 && (category.name || choices.length > 1)) {
                            <ion-item
                                class="category-header"
                                [class.expanded]="category.expanded"
                                [lines]="getCategoryLines(category)"
                                (click)="toggleCategory(category, $event)"
                            >
                                @if (category.expanded) {
                                    <ion-icon name="remove" class="expander"></ion-icon>
                                } @else {
                                    <ion-icon name="add" class="expander"></ion-icon>
                                }
                                <ion-label>{{ category.name }}</ion-label>
                            </ion-item>
                        }
                        @if (category.name || choices.length > 1) {
                            <div [@verticalCollapse]="category.expanded ? 'no' : 'yes'">
                                @for (item of category.items; track item.key) {
                                    <ion-item
                                        class="category-item"
                                        detail="false"
                                        [lines]="getItemLines(category, item)"
                                        [disabled]="item.disabled"
                                        (click)="selectValue(item.key)"
                                    >
                                        <ion-radio [mode]="currentMode" [value]="item.key" [disabled]="item.disabled">{{
                                            item.name
                                        }}</ion-radio>
                                    </ion-item>
                                }
                            </div>
                        } @else {
                            @for (item of category.items; track item.key) {
                                <ion-item
                                    class="category-item"
                                    detail="false"
                                    [lines]="getItemLines(category, item)"
                                    [disabled]="item.disabled"
                                    (click)="selectValue(item.key)"
                                >
                                    <ion-radio [mode]="currentMode" [value]="item.key" [disabled]="item.disabled">{{
                                        item.name
                                    }}</ion-radio>
                                </ion-item>
                            }
                        }
                    }
                </ion-radio-group>
            </ion-list>
        </ion-content>
    `,
    styles: [
        `
            :host-context(.ios) ion-toolbar ion-button ion-icon {
                margin-inline-end: 0;
            }

            ion-list {
                padding-bottom: 3rem;
            }

            ion-item {
                cursor: pointer;
            }

            ion-item[disabled] {
                opacity: 0.7;
            }

            ion-item.category-header {
                font-weight: 600;
                --color: var(--ion-color-step-600);
            }

            ion-item.category-header ion-icon.expander {
                margin-right: 0.7em;
            }

            :host-context(.md) ion-item.item-lines-none {
                --inner-padding-bottom: 1px;
            }

            :host-context(.ios) ion-item.item-lines-none {
                --inner-padding-bottom: 0.55px;
            }

            :host-context(.ios) ion-toolbar {
                --border-width: 0 !important;
            }

            :host-context(.ios) ion-toolbar.search-bar {
                padding-top: 0 !important;
            }

            ion-item.reset-item {
                --min-height: 3em;
            }

            ion-label {
                white-space: normal !important;
            }
            ion-radio {
                --color-checked: var(--ion-text-color);
            }
        `,
    ],
    animations: [verticalCollapseAnimation],
})
export class CategorySelectModalComponent implements OnInit {
    readonly title = input.required<string>();
    readonly cancelButton = input('');
    readonly choices = input<CategorySelectModalCategory[]>([]);
    readonly canSearch = input<boolean | undefined>(undefined);
    readonly initialValue = input<string | undefined>(undefined);
    readonly defaultValues = input<any[] | undefined>(undefined);
    readonly dismissOnClick = input(false);
    readonly mode = input<'ios' | 'md' | undefined>();

    readonly value = signal<any>(undefined);
    readonly searchQuery = signal('');

    readonly effectiveCanSearch = computed(() => this.canSearch() ?? sum(this.choices().map((c) => c.items.length)) >= 6);

    readonly visibleChoices = computed(() => {
        const query = this.searchQuery();
        const allChoices = this.choices();
        if (!query) {
            return allChoices;
        }

        const fuse = this.fuse();
        if (!fuse) {
            return allChoices;
        }

        return [
            {
                name: '',
                expanded: true,
                items: fuse.search(query).map((result) => ({
                    ...result.item.item,
                    name: result.item.item.name + (result.item.category.name ? ` (${result.item.category.name})` : ''),
                })),
            },
        ];
    });

    public readonly uiOptions = inject(UiOptions);
    private readonly logger = inject<Logger>(LOGGER);
    private readonly modalCtrlr = inject(ModalController);
    private previousResizeMode: KeyboardResize | undefined;

    private readonly fuse = computed(() => {
        if (!this.effectiveCanSearch()) {
            return undefined;
        }
        const fuseItems = this.choices()
            .map((category) => category.items.map((item) => ({ item, category })))
            .reduce((a, b) => a.concat(b), []);
        return new Fuse(fuseItems, {
            shouldSort: true,
            findAllMatches: true,
            threshold: 0.6,
            location: 0,
            distance: 100,
            minMatchCharLength: 1,
            keys: [
                { name: 'item.name', weight: 0.7 },
                { name: 'category.name', weight: 0.3 },
            ],
        });
    });

    private readonly searchbar = viewChild<Components.IonSearchbar & { el: HTMLElement }>('theSearchbar');

    constructor() {
        afterNextRender(() => {
            if (!this.effectiveCanSearch()) {
                return;
            }
            if (this.uiOptions.isAndroid() || this.uiOptions.isIos()) {
                return;
            }
            let attempts = 30; // on Electron, doesn't work the first few times
            const focus = () => {
                const searchbar = this.searchbar();
                if (searchbar) {
                    searchbar.setFocus();
                    const isFocused = searchbar.el.contains(document.activeElement);
                    if (!isFocused && attempts > 0) {
                        --attempts;
                        window.requestAnimationFrame(focus);
                    }
                }
            };
            window.requestAnimationFrame(focus);
        });
    }

    ngOnInit() {
        this.value.set(this.initialValue());

        const choices = this.choices();
        for (const category of choices) {
            if (typeof category.expanded === 'undefined') {
                category.expanded = choices.length === 1 || this.isSelectedCategory(category);
            }
        }
    }

    // Scope KeyboardResize.None to this modal so dismissing with the keyboard up is a smooth single
    // slide rather than the two-stage drop the native resize causes; restore the prior mode on leave.
    async ionViewWillEnter() {
        if (this.uiOptions.isIos()) {
            this.previousResizeMode = (await Keyboard.getResizeMode()).mode;
            await Keyboard.setResizeMode({ mode: KeyboardResize.None });
        }
    }

    async ionViewDidLeave() {
        if (this.uiOptions.isIos() && this.previousResizeMode) {
            await Keyboard.setResizeMode({ mode: this.previousResizeMode });
            this.previousResizeMode = undefined;
        }
    }

    selectValue(v: any) {
        if (this.choices().some((c) => c.items.some((i) => i.key === v && i.disabled))) {
            return;
        }
        this.value.set(v);
        if (this.dismissOnClick()) {
            this.modalCtrlr.dismiss(v, undefined, 'CategorySelectModalComponent').catch(this.logger.warn);
        }
    }

    onSearchBarKeyDown = (ev: KeyboardEvent) => {
        if (ev && ev.key === 'Escape') {
            this.searchQuery.set('');
        }
    };

    getCategoryLines(category: CategorySelectModalCategory) {
        const choices = this.choices();
        if (category.expanded && category.items.length) {
            return 'inset';
        }
        if (choices.indexOf(category) < choices.length - 1) {
            return 'full';
        }
        return 'none';
    }

    getItemLines(category: CategorySelectModalCategory, item: CategorySelectModalItem) {
        const choices = this.choices();
        if (category.items.indexOf(item) < category.items.length - 1) {
            return 'inset';
        }
        if (choices.indexOf(category) < choices.length - 1) {
            return 'full';
        }
        return 'none';
    }

    isSelectedCategory(category: CategorySelectModalCategory) {
        return category.items.some((item) => item.key === this.value());
    }

    toggleCategory(category: CategorySelectModalCategory, event: Event) {
        category.expanded = !category.expanded;
    }

    cancel() {
        this.modalCtrlr.dismiss(undefined, undefined, 'CategorySelectModalComponent').catch(this.logger.warn);
    }
}
